import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import { AppShell } from './components/layout/AppShell';
import { RoleGate } from './components/RoleGate';
import { useIsAdmin } from './hooks/useRole';
import { useOnboarding } from './hooks/useOnboarding';

// Lazy-load pages to keep initial bundle small
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminExchanges from './pages/AdminExchanges';
import MyExchanges from './pages/MyExchanges';
import AddExchangeForm from './pages/AddExchangeForm';
import PortfolioView from './pages/PortfolioView';
import ProfilePage from './pages/ProfilePage';
import OnboardingPage from './pages/OnboardingPage';
import TaxPage from './pages/TaxPage';
function AppRoutes() {
  const isAdmin = useIsAdmin();
  const { isLoaded, onboardingComplete } = useOnboarding();

  // Wait until Clerk has resolved user metadata
  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  // New user — redirect every path to onboarding (no Header)
  // Admins skip onboarding entirely
  if (!isAdmin && !onboardingComplete) {
    return (
      <Routes>
        <Route path="*" element={<OnboardingPage />} />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Routes>
        {/* Root — redirect based on role */}
        <Route path="/" element={<Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />} />

        {/* User routes */}
        <Route path="/dashboard" element={<UserDashboard />} />
        <Route path="/exchanges" element={<MyExchanges />} />
        <Route path="/exchanges/new" element={<AddExchangeForm />} />
        <Route path="/portfolio" element={<PortfolioView />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/tax" element={<TaxPage />} />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <RoleGate role="admin">
              <AdminDashboard />
            </RoleGate>
          }
        />
        <Route
          path="/admin/exchanges"
          element={
            <RoleGate role="admin">
              <AdminExchanges />
            </RoleGate>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public auth routes — accessible without login */}
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />

      {/* Everything else — protected */}
      <Route
        path="*"
        element={
          <>
            <SignedIn>
              <AppRoutes />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        }
      />
    </Routes>
  );
}
